import { LinearProgress } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { CSSProperties, useState } from "react";
import { AssetDetailsDTO, AssetKey } from "../../dto/AssetDTO";
import "./AssetCard.css";
import AssetNameField from "./AssetNameField";
import AssetRowMenu from "./AssetRowMenu";
import { ReactComponent as FileIcon } from "./assets/file.svg";
import classNames from "classnames";
interface AssetCardProps {
  asset: AssetDetailsDTO;
  onSelect?: (asset: AssetDetailsDTO) => void;
  /** Double-click confirm — the same action as the footer primary. */
  onConfirm?: (asset: AssetDetailsDTO) => void;
  style?: CSSProperties;
  type?: AssetKey;
  isSelected?: boolean;
  /** Target for the search field's aria-activedescendant. */
  optionId?: string;
  /** Multi-select mode: every pickable card carries a checkbox. */
  showCheckbox?: boolean;
  isChecked?: boolean;
  /** Inline rename, driven from the explorer body so only one runs at a time. */
  isRenaming?: boolean;
  onRenameStart?: () => void;
  onRenameSubmit?: (name: string) => void;
  onRenameCancel?: () => void;
}

export default function AssetCard(props: AssetCardProps) {
  const { asset, type, isSelected, showCheckbox, isChecked } = { ...props };
  const [isLoading, setIsLoading] = useState<boolean>(true);

  /**
   * Present but unpickable because the explorer is scoped to another type.
   * Distinct from `isBlocked`, which also covers failed and in-flight
   * uploads — those already have their own treatment.
   */
  const outOfScope = type !== undefined && asset.key !== type;

  const isBlocked =
    asset.key === AssetKey.Loading ||
    asset.key === AssetKey.Unsupported ||
    (type !== undefined && asset.key !== type);

  const onClick = () => {
    if (isBlocked) return;
    props.onSelect?.(asset);
  };

  const onDoubleClick = () => {
    if (isBlocked) return;
    props.onConfirm?.(asset);
  };

  const preview = () => {
    if (props.asset.key === AssetKey.Unsupported) {
      return <FileIcon className="assetcard-unsupported-icon w-[40px] h-[40px]" />;
    } else if (props.asset.key === AssetKey.Loading) {
      return <FileIcon className="assetcard-loading-icon w-[40px] h-[40px]" />;
    } else {
      return (
        <>
          {isLoading && <CircularProgress size={22} />}
          <img
            className={classNames(
              "pointer-events-none select-none object-contain w-full h-full",
              { "assetcard-hide-image": isLoading }
            )}
            alt="Thumbnail"
            src={props.asset.variants[0]?.downloadUri}
            onLoad={() => {
              setIsLoading(false);
            }}
          />
        </>
      );
    }
  };

  const title = props.asset.name;

  const getSubtitle = () => {
    if (asset.key === AssetKey.Unsupported) {
      return "unsupported";
    } else if (asset.key === AssetKey.Loading) {
      return (
        <div className="assetcard-progress">
          <LinearProgress />
        </div>
      );
    } else {
      return asset.updatedBy;
    }
  };

  const labelContent =
    props.asset.key !== AssetKey.Unsupported
      ? "v" + props.asset.version.major + "." + props.asset.version.minor
      : "failed";

  return (
    <div
      id={props.optionId}
      role="option"
      aria-selected={!!isSelected}
      data-testid={
        props.asset.key === AssetKey.Loading
          ? "asset-card-loading"
          : `asset-card-${props.asset.assetId || props.asset.id}`
      }
      // Deliberately the same class list as the prop library's tile
      // (PropLibraryResults) so the two grids read as one system — including
      // the selected treatment. Any change here belongs in both.
      className={classNames(
        "assetcard-root group w-full h-full rounded-[8px] border flex flex-col overflow-hidden text-left transition-colors",
        {
          "border-creator-error bg-creator-error-washed":
            asset.key === AssetKey.Unsupported,
          "border-creator-primary bg-creator-primary-washed":
            isSelected && asset.key !== AssetKey.Unsupported,
          "border-creator-border bg-creator-surface":
            !isSelected && asset.key !== AssetKey.Unsupported,
          // No hover border on something that cannot be picked — the hover
          // was the old "is this selectable?" test, and it lied.
          "hover:border-[var(--pi-menu-text-color-1-faded)]":
            !isSelected && asset.key !== AssetKey.Unsupported && !outOfScope,
          "cursor-pointer": props.onSelect && !isBlocked,
          "cursor-progress": asset.key === AssetKey.Loading,
          "cursor-not-allowed":
            asset.key === AssetKey.Unsupported ||
            (type !== undefined && asset.key !== type),
        }
      )}
      style={props.style}
    >
      <div
        className={classNames(
          "assetcard-preview h-[104px] w-full shrink-0 bg-interaction-softBackground flex items-center justify-center overflow-hidden",
          { "opacity-45": outOfScope }
        )}
        onClick={onClick}
        onDoubleClick={onDoubleClick}
      >
        {preview()}
      </div>

      {showCheckbox && !isBlocked && (
        <span
          aria-hidden
          className={classNames(
            "absolute top-[8px] left-[8px] w-[18px] h-[18px] rounded-[4px] border flex items-center justify-center text-[12px] leading-none z-10 pointer-events-none",
            isChecked
              ? "bg-creator-primary border-creator-primary text-white"
              : "border-creator-border bg-creator-surface"
          )}
        >
          {isChecked ? "✓" : ""}
        </span>
      )}

      {/* The menu is deliberately outside the dimmed region: an out-of-scope
          file stays on screen precisely so it can still be renamed, localized
          or deleted. */}
      <div className="flex-1 min-h-0 px-[10px] py-[8px] flex flex-col gap-[6px]">
        <div
          className={classNames("flex items-center gap-[8px]", {
            "opacity-45": outOfScope,
          })}
        >
          {props.isRenaming ? (
            <div className="flex-1 min-w-0">
              <AssetNameField
                name={title}
                onSubmit={(name) => props.onRenameSubmit?.(name)}
                onCancel={() => props.onRenameCancel?.()}
              />
            </div>
          ) : (
            <div
              className="assetcard-assetname flex-1 min-w-0 text-[13px] leading-[18px] text-creator-text truncate"
              onClick={onClick}
              onDoubleClick={onDoubleClick}
            >
              {title}
            </div>
          )}
          <span
            className={classNames(
              "shrink-0 px-[6px] py-[2px] rounded-[4px] border text-[9px] leading-[12px] whitespace-nowrap",
              asset.key === AssetKey.Unsupported
                ? "border-creator-error text-creator-error"
                : "border-creator-border text-creator-text-sub"
            )}
          >
            {labelContent}
          </span>
        </div>
        <div className="flex items-center gap-[4px] min-h-[16px]">
          <div
            className={classNames(
              "assetcard-uploadedby flex-1 min-w-0 truncate text-[11px] leading-[16px] text-creator-text-sub",
              { "opacity-45": outOfScope }
            )}
            onClick={onClick}
            onDoubleClick={onDoubleClick}
          >
            {getSubtitle()}
          </div>
          <AssetRowMenu asset={asset} onRename={props.onRenameStart} />
        </div>
      </div>
    </div>
  );
}
